import { useState } from 'react';

interface ContactSectionProps {
  contact: {
    title: string;
    subtitle: string;
    email: string;
    wechat: string;
    weibo: string;
    address: string;
    businessHours: string;
  };
  formUrl: string;
}

export default function ContactSection({ contact, formUrl }: ContactSectionProps) {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setSubmitStatus('success');
      setSubmitting(false);
      form.reset();
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = (parsed as Record<string, unknown>)?.meta as Record<string, unknown> | undefined;
      const msgText = serverMsg?.message as string || '';

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else if (msgText.includes('spam') || (parsed && (parsed as Record<string, unknown>).code !== 'OK')) {
        setSubmitStatus('error');
        setFormError(msgText || '提交失败，请稍后重试');
      } else {
        setSubmitStatus('error');
        setFormError(msgText || '提交失败，请稍后重试');
      }
    } catch {
      setSubmitStatus('error');
      setFormError('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
            {contact.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-500">
            {contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-background-950 rounded-lg p-6 md:p-10 text-white">
            <h3 className="text-lg md:text-xl font-bold font-heading mb-6">平台信息</h3>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-400">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">电子邮箱</p>
                  <p className="text-sm text-white/80">{contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-400">
                  <i className="ri-wechat-line"></i>
                </span>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">微信公众号</p>
                  <p className="text-sm text-white/80">{contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-400">
                  <i className="ri-weibo-line"></i>
                </span>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">官方微博</p>
                  <p className="text-sm text-white/80">{contact.weibo}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-400">
                  <i className="ri-map-pin-line"></i>
                </span>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">办公地址</p>
                  <p className="text-sm text-white/80">{contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-accent-400">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">工作时间</p>
                  <p className="text-sm text-white/80">{contact.businessHours}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 md:p-10">
            <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-6">
              商务合作咨询
            </h3>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-accent-50 text-accent-800 rounded-lg text-sm text-center">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1">
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                提交成功！我们会尽快与您联系。
              </div>
            )}
            {submitStatus === 'error' && formError && (
              <div className="mb-6 p-4 bg-primary-50 text-primary-700 rounded-lg text-sm">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  您的姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  公司名称
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入公司名称"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  合作需求 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请描述您的合作需求（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字</p>
              </div>
              <div className="form-hp-label">
                <label htmlFor="website_alt">网站</label>
                <input
                  type="text"
                  id="website_alt"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary-500 text-white py-3 rounded-lg text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {submitting ? '提交中...' : '提交合作咨询'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}